/**
 * /pipeline-facility/sites/new — add a pipeline or facility
 * (POST /pipeline-facility/sites). A full page, not a modal. On save the new
 * site's own page replaces this one, so Back lands on the register.
 *
 * PF_ADMIN only. Any other role is told so here rather than handed a form the
 * API would refuse.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreatePfSite, usePfCustomers, usePfMeta } from '../../hooks/usePipelineFacility';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminPf, pfErrorMessage } from '../../lib/pipeline-facility';
import { useBackToList } from '../downhole/recordPage';
import { SiteForm } from './SiteForm';
import { PF_EYEBROW, PfEmpty, PfLoading, PfPanel } from './PfBits';

export default function PfNewSiteScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/pipeline-facility/sites');
  const { user } = useAuth();
  const canAdd = canAdminPf(user?.role);
  const meta = usePfMeta();
  const customers = usePfCustomers(canAdd);
  const create = useCreatePfSite();
  const [error, setError] = useState<string | null>(null);
  const metaFailure = queryLoadFailure(meta);
  const customersFailure = queryLoadFailure(customers);

  return (
    <div data-testid="pf-new-site-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={PF_EYEBROW} title="New site" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PfPanel eyebrow="Site register" title="Pipeline or facility">
            {!canAdd ? (
              <PfEmpty>Only an admin or office staff can add a site.</PfEmpty>
            ) : meta.isLoading || customers.isLoading ? (
              <PfLoading label="Loading form" />
            ) : metaFailure || customersFailure ? (
              <LoadFailureNotice
                error={queryFailureError(metaFailure ? meta : customers)}
                kind={(metaFailure ?? customersFailure) || 'unreachable'}
                subject={metaFailure ? 'the site form' : 'your customers'}
              />
            ) : (customers.data ?? []).length === 0 ? (
              <PfEmpty>A site belongs to a customer, and there are no customers yet. Add one under Clients first.</PfEmpty>
            ) : (
              <SiteForm
                site={null}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                customers={customers.data ?? []}
                pending={create.isPending}
                error={error}
                submitLabel="Create site"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success('Site created');
                      if (res.data?.id) navigate(`/pipeline-facility/sites/${res.data.id}`, { replace: true });
                      else navigate('/pipeline-facility/sites', { replace: true });
                    },
                    onError: (err) => setError(pfErrorMessage(err, 'The site could not be created.')),
                  });
                }}
              />
            )}
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
